import type { ReactNode } from 'react';

import type { PictureInferenceFilterOptionGroup } from 'horizon/routes/Inspections/InspectionDetail/pictureInferenceFilter.utils';

import type { ImageProps } from '../Image';

import { AssetOverlayTool } from './AssetOverlayTool';
import {
  ButtonContainer,
  ControlsContainer,
  ThumbnailButtonContainer,
  ThumbnailControlsContainer,
} from './Controls.style';
import { CustomRotate } from './CustomRotate';
import { Filters } from './Filters';
import { InferenceFilterTool } from './InferenceFilterTool';
import { Measurement } from './Measurement';
import { ReviewedTool } from './ReviewedTool';
import { Rotate } from './Rotate';
import { Temperature } from './Temperature';
import { Zoom } from './Zoom';

type ControlsProps = {
  onChange: (arg: any) => void;
  onClickLevels: () => void;
  value?: {
    rotate?: boolean;
    customRotationDeg?: number;
    customRotationActive?: boolean;
    filter?: string;
    levels?: { url?: string; width?: number; height?: number; a?: number; b?: number };
    measure?: boolean;
    temperature?: boolean;
    edit?: boolean;
    assetOverlay?: boolean;
    reviewed?: boolean;
    selectedInferenceDetectionTypeIds?: string[];
    selectedInferenceDeltaTMin?: number;
    selectedInferenceConfidenceMin?: number;
  };
  isThumbnail?: boolean;
  pictureId?: string;
  pictureGlobalId?: string;
  refetch: ImageProps['refetch'];
  hasHomography?: boolean;
  inferenceFilterOptions?: PictureInferenceFilterOptionGroup[];
  rotationEnabled?: boolean;
  bladeRotationTitle?: string;
  additionalControl?: ReactNode;
};

export const Controls = ({
  onChange,
  onClickLevels,
  value,
  isThumbnail = false,
  pictureId,
  pictureGlobalId,
  refetch,
  hasHomography = false,
  inferenceFilterOptions = [],
  rotationEnabled,
  bladeRotationTitle,
  additionalControl,
}: ControlsProps) => {
  const {
    rotate,
    customRotationDeg,
    customRotationActive,
    filter,
    levels,
    measure,
    temperature,
    assetOverlay,
    reviewed,
    selectedInferenceDetectionTypeIds,
    selectedInferenceDeltaTMin,
    selectedInferenceConfidenceMin,
    edit,
  } = value ?? {};

  if (isThumbnail) {
    return (
      <ThumbnailControlsContainer>
        <ThumbnailButtonContainer>
          <Zoom onChange={onChange} />
        </ThumbnailButtonContainer>
        <ThumbnailButtonContainer>
          <Rotate
            onChange={onChange}
            title={bladeRotationTitle}
            value={rotate}
            disabled={!rotationEnabled}
          />
        </ThumbnailButtonContainer>
      </ThumbnailControlsContainer>
    );
  }

  return (
    <ControlsContainer>
      {reviewed !== undefined && (
        <ButtonContainer>
          <ReviewedTool
            pictureId={pictureId}
            pictureGlobalId={pictureGlobalId}
            reviewed={reviewed}
            disabled={edit}
            refetch={refetch}
          />
        </ButtonContainer>
      )}
      {assetOverlay !== undefined && (
        <ButtonContainer>
          <AssetOverlayTool onChange={onChange} value={assetOverlay} disabled={edit} />
        </ButtonContainer>
      )}
      {selectedInferenceDetectionTypeIds !== undefined && (
        <ButtonContainer>
          <InferenceFilterTool
            onChange={onChange}
            value={selectedInferenceDetectionTypeIds}
            selectedInferenceDeltaTMin={selectedInferenceDeltaTMin}
            selectedInferenceConfidenceMin={selectedInferenceConfidenceMin}
            options={inferenceFilterOptions}
            disabled={Boolean(edit)}
          />
        </ButtonContainer>
      )}
      {additionalControl && <ButtonContainer>{additionalControl}</ButtonContainer>}
      {temperature !== undefined && (
        <ButtonContainer>
          <Temperature onChange={onChange} value={temperature} disabled={edit} />
        </ButtonContainer>
      )}
      {measure !== undefined && (
        <ButtonContainer>
          <Measurement
            onChange={onChange}
            value={measure}
            disabled={edit}
            hasHomography={hasHomography}
          />
        </ButtonContainer>
      )}
      <ButtonContainer>
        <CustomRotate active={customRotationActive} onChange={onChange} value={customRotationDeg} />
      </ButtonContainer>
      <ButtonContainer>
        <Rotate
          onChange={onChange}
          title={bladeRotationTitle}
          value={rotate}
          disabled={!rotationEnabled}
        />
      </ButtonContainer>
      <ButtonContainer>
        <Filters
          onChange={onChange}
          onClickLevels={onClickLevels}
          value={filter}
          levels={levels}
          pictureId={pictureId}
          pictureGlobalId={pictureGlobalId}
          refetch={refetch}
        />
      </ButtonContainer>
      <ButtonContainer $zoomControl>
        <Zoom onChange={onChange} />
      </ButtonContainer>
    </ControlsContainer>
  );
};
